'use client';

import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Button, Divider, Modal, PageLoading, Textarea, type BadgeTone } from '@/ui';
import { cn } from '@/lib/cn';
import { fmtDateTime } from '@/lib/billing-ui';

// Customer-facing labels for the closer-managed status vocabulary. Staff pick
// from the same words, so what they set is what the customer reads.
export const CHANGE_REQUEST_STATUS_LABELS: Record<string, string> = {
  open: 'Received',
  in_progress: 'In progress',
  resolved: 'Done',
  closed: 'Not planned',
};

export const CHANGE_REQUEST_STATUS_TONES: Record<string, BadgeTone> = {
  open: 'neutral',
  in_progress: 'accent',
  resolved: 'success',
  closed: 'outline',
};

export interface ThreadMessage {
  id: string;
  body: string;
  createdAt: string;
  // Display name resolved by the caller: the portal shows staff as the
  // company; the CRM shows usernames and the customer's name.
  authorLabel: string;
  isOwn: boolean;
}

/**
 * Which request's thread is open and its messages (null until the detail
 * lands; an unreachable thread shows as empty rather than blocking the modal).
 * `toMessage` resolves the display name per host.
 */
export function useChangeRequestThread<R extends { id: string }, M>(
  api: {
    detail: (id: string) => Promise<{ request: R; messages: M[] }>;
    send: (id: string, body: string) => Promise<M>;
  },
  toMessage: (message: M, request: R) => ThreadMessage
) {
  const [openRequest, setOpenRequest] = useState<R | null>(null);
  const [thread, setThread] = useState<M[] | null>(null);
  // The id whose detail is wanted; a detail() that resolves for any other id
  // (row switched, modal closed) must not reopen the modal on it.
  const requestedId = useRef<string | null>(null);

  function openThread(request: R): void {
    requestedId.current = request.id;
    setOpenRequest(request);
    setThread(null);
    api
      .detail(request.id)
      .then((detail) => {
        if (requestedId.current !== request.id) return;
        setOpenRequest(detail.request);
        setThread(detail.messages);
      })
      .catch(() => {
        if (requestedId.current === request.id) setThread([]);
      });
  }

  function closeThread(): void {
    requestedId.current = null;
    setOpenRequest(null);
    setThread(null);
  }

  async function sendMessage(body: string): Promise<void> {
    if (!openRequest) return;
    const message = await api.send(openRequest.id, body);
    setThread((prev) => [...(prev ?? []), message]);
  }

  const messages = openRequest && thread ? thread.map((message) => toMessage(message, openRequest)) : null;
  return { openRequest, messages, openThread, closeThread, sendMessage };
}

/** Modal around ChangeRequestThread; `messages` is null while the thread loads. */
export function ChangeRequestThreadModal({
  request,
  title,
  messages,
  loading = <PageLoading />,
  onSend,
  onClose,
}: {
  request: { description: string } | null;
  title: string;
  messages: ThreadMessage[] | null;
  loading?: ReactNode;
  onSend: (body: string) => Promise<void>;
  onClose: () => void;
}) {
  return (
    <Modal open={request !== null} title={title} onClose={onClose}>
      {request ? (
        messages === null ? (
          loading
        ) : (
          <ChangeRequestThread description={request.description} messages={messages} onSend={onSend} />
        )
      ) : null}
    </Modal>
  );
}

// Conversation body shared by the portal and CRM request modals: the original
// request description, the message list, and a composer.
function ChangeRequestThread({
  description,
  messages,
  onSend,
}: {
  description: string;
  messages: ThreadMessage[];
  onSend: (body: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!draft.trim()) return;
    setError('');
    setSending(true);
    try {
      await onSend(draft.trim());
      setDraft('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the message');
    }
    setSending(false);
  }

  return (
    <div className="grid gap-4">
      <p className="text-sm whitespace-pre-wrap text-zinc-300">{description}</p>
      <Divider />
      {messages.length === 0 ? (
        <p className="text-xs text-zinc-500">No replies yet.</p>
      ) : (
        <div className="grid gap-3">
          {messages.map((message) => (
            <div key={message.id} className="grid gap-1">
              <div className="flex items-baseline gap-2">
                <span className={cn('text-sm font-medium', message.isOwn ? 'text-zinc-100' : 'text-accent-bright')}>
                  {message.authorLabel}
                </span>
                <span className="text-xs text-zinc-500 tabular-nums">{fmtDateTime(message.createdAt)}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap text-zinc-300">{message.body}</p>
            </div>
          ))}
        </div>
      )}
      <form className="grid gap-2" onSubmit={onSubmit}>
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={3}
          maxLength={5000}
          placeholder="Write a reply"
          aria-label="Reply"
        />
        {error ? <p className="text-danger text-sm">{error}</p> : null}
        <div className="flex justify-end">
          <Button variant="primary" type="submit" loading={sending} disabled={!draft.trim()}>
            Send
          </Button>
        </div>
      </form>
    </div>
  );
}
